// src/redact.ts
var RULES = [
  [/(?:)(?<prefix>authorization:\s*bearer\s+)[A-Za-z0-9._\-]+/gi, "$<prefix>***"],
  [/eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}/g, "***JWT***"],
  [/sk-[A-Za-z0-9]{20,}/g, "***API_KEY***"],
  [/("(?:password|secret|token|api_key|apiKey|private_key)"\s*:\s*)"[^"]*"/g, '$1"***"'],
  [/\b(?:\d[ \-]*?){13,16}\b/g, "***CARD***"]
];
function redact(value) {
  let out = value;
  for (const [pat, repl] of RULES) {
    out = out.replace(pat, repl);
  }
  return out;
}

// src/client.ts
var LogsClient = class {
  constructor(opts) {
    this.queue = [];
    this.timer = null;
    this.stopped = false;
    this.opts = {
      endpoint: "/api/v2/logs/frontend",
      user: {},
      flushIntervalMs: 5e3,
      batchMaxEvents: 50,
      breadcrumbMax: 50,
      maxBodyBytes: 65536,
      captureNetwork: true,
      captureWebVitals: true,
      sampleRate: 1,
      getTraceId: () => void 0,
      ...opts
    };
    this.timer = setInterval(() => this.flush(), this.opts.flushIntervalMs);
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush(true));
      window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") this.flush(true);
      });
    }
  }
  capture(ev) {
    if (this.stopped) return;
    if (Math.random() > this.opts.sampleRate) return;
    const enriched = {
      ...ev,
      message: redact(ev.message),
      timestamp: ev.timestamp ?? (/* @__PURE__ */ new Date()).toISOString(),
      url: ev.url ?? (typeof location !== "undefined" ? location.href : void 0),
      user_agent: ev.user_agent ?? (typeof navigator !== "undefined" ? navigator.userAgent : void 0),
      user_id: ev.user_id ?? this.opts.user?.id,
      trace_id: ev.trace_id ?? this.opts.getTraceId?.()
    };
    this.queue.push(enriched);
    if (ev.level === "error") {
      setTimeout(() => this.flush(), 0);
    } else if (this.queue.length >= this.opts.batchMaxEvents) {
      this.flush();
    }
  }
  async flush(useBeacon = false) {
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.opts.batchMaxEvents);
    const body = JSON.stringify({ service: this.opts.service, events });
    try {
      if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon(this.opts.endpoint, new Blob([body], { type: "application/json" }));
        return;
      }
      await fetch(this.opts.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true
      });
    } catch {
      if (this.queue.length < this.opts.batchMaxEvents * 4) {
        this.queue.unshift(...events);
      }
    }
  }
  get endpoint() {
    return this.opts.endpoint;
  }
  stop() {
    this.stopped = true;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
};

// src/breadcrumbs.ts
var BreadcrumbQueue = class {
  constructor(max = 50) {
    this.max = max;
    this.buf = [];
  }
  push(crumb) {
    this.buf.push(crumb);
    if (this.buf.length > this.max) this.buf.splice(0, this.buf.length - this.max);
  }
  snapshot() {
    return this.buf.slice();
  }
  clear() {
    this.buf = [];
  }
};

// src/errors.ts
function installErrorHandlers(client, crumbs) {
  window.addEventListener("error", (ev) => {
    client.capture({
      level: "error",
      message: ev.message || String(ev.error ?? "Unknown error"),
      metadata: {
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        stack: ev.error?.stack
      },
      breadcrumbs: crumbs.snapshot()
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    client.capture({
      level: "error",
      message: `UnhandledPromiseRejection: ${reason?.message ?? String(reason)}`,
      metadata: { stack: reason?.stack },
      breadcrumbs: crumbs.snapshot()
    });
  });
  const origs = { log: console.log, warn: console.warn, error: console.error };
  ["log", "warn", "error"].forEach((method) => {
    const orig = origs[method];
    console[method] = (...args) => {
      try {
        crumbs.push({
          at: (/* @__PURE__ */ new Date()).toISOString(),
          category: "console",
          message: args.map((a) => typeof a === "string" ? a : JSON.stringify(a)).join(" ").slice(0, 500)
        });
      } catch {
      }
      orig.apply(console, args);
    };
  });
  window.addEventListener("click", (ev) => {
    const t = ev.target;
    if (!t) return;
    const id = t.id ? `#${t.id}` : "";
    crumbs.push({
      at: (/* @__PURE__ */ new Date()).toISOString(),
      category: "click",
      message: `${t.tagName.toLowerCase()}${id}`
    });
  }, true);
  const origPushState = history.pushState;
  history.pushState = function(...args) {
    crumbs.push({ at: (/* @__PURE__ */ new Date()).toISOString(), category: "navigation", message: String(args[2] ?? "") });
    return origPushState.apply(this, args);
  };
}

// src/network.ts
var MAX_BODY = 65536;
function installNetworkInterceptor(client, crumbs, ignoreUrls = []) {
  const shouldIgnore = (url) => url.includes("/api/v2/logs/frontend") || url.includes(client.endpoint) || ignoreUrls.some((p) => url.includes(p));
  const g = globalThis;
  const origFetch = g.fetch.bind(g);
  g.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    if (shouldIgnore(url)) return origFetch(input, init);
    const started = performance.now();
    const method = (init?.method ?? "GET").toUpperCase();
    try {
      const resp = await origFetch(input, init);
      const took = Math.round(performance.now() - started);
      crumbs.push({
        at: (/* @__PURE__ */ new Date()).toISOString(),
        category: "fetch",
        message: `${method} ${url} ${resp.status} ${took}ms`,
        data: { url, method, status: resp.status, took_ms: took }
      });
      if (!resp.ok && resp.status >= 400) {
        let body = "";
        try {
          body = (await resp.clone().text()).slice(0, MAX_BODY);
        } catch {
        }
        client.capture({
          level: "error",
          message: `HTTP ${resp.status} ${method} ${url}`,
          metadata: { method, url, status: resp.status, took_ms: took, body }
        });
      }
      return resp;
    } catch (err) {
      const took = Math.round(performance.now() - started);
      client.capture({
        level: "error",
        message: `Fetch failed: ${method} ${url}`,
        metadata: { method, url, took_ms: took, error: err?.message }
      });
      throw err;
    }
  };
  if (typeof g.XMLHttpRequest === "undefined") return;
  const OrigXHR = g.XMLHttpRequest;
  function InstrumentedXHR() {
    const xhr = new OrigXHR();
    let meta = null;
    const origOpen = xhr.open.bind(xhr);
    xhr.open = function(method, url, ...rest) {
      meta = { method: method.toUpperCase(), url: String(url), started: performance.now() };
      return origOpen(method, url, ...rest);
    };
    xhr.addEventListener("loadend", () => {
      if (!meta || shouldIgnore(meta.url)) return;
      const took = Math.round(performance.now() - meta.started);
      crumbs.push({
        at: (/* @__PURE__ */ new Date()).toISOString(),
        category: "xhr",
        message: `${meta.method} ${meta.url} ${xhr.status} ${took}ms`,
        data: { method: meta.method, url: meta.url, status: xhr.status, took_ms: took }
      });
      if (xhr.status >= 400) {
        client.capture({
          level: "error",
          message: `XHR ${xhr.status} ${meta.method} ${meta.url}`,
          metadata: {
            method: meta.method,
            url: meta.url,
            status: xhr.status,
            took_ms: took,
            body: String(xhr.responseText ?? "").slice(0, MAX_BODY)
          }
        });
      }
    });
    return xhr;
  }
  InstrumentedXHR.prototype = OrigXHR.prototype;
  g.XMLHttpRequest = InstrumentedXHR;
}

// src/vitals.ts
function installWebVitals(client) {
  if (typeof PerformanceObserver === "undefined") return;
  const report = (name, value, meta) => {
    client.capture({ level: "info", message: `webvitals.${name}=${value.toFixed(2)}`, metadata: { metric: name, value, ...meta } });
  };
  try {
    const po = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) report("LCP", last.startTime);
    });
    po.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
  }
  try {
    const po = new PerformanceObserver((list) => {
      for (const ev of list.getEntries()) {
        report("FID", ev.processingStart - ev.startTime, { name: ev.name });
        break;
      }
    });
    po.observe({ type: "first-input", buffered: true });
  } catch {
  }
  try {
    let cls = 0;
    const po = new PerformanceObserver((list) => {
      for (const ent of list.getEntries()) {
        if (!ent.hadRecentInput) cls += ent.value;
      }
    });
    po.observe({ type: "layout-shift", buffered: true });
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") report("CLS", cls);
    });
  } catch {
  }
  try {
    const po = new PerformanceObserver((list) => {
      let worst = 0;
      for (const ev of list.getEntries()) {
        if (ev.duration > worst) worst = ev.duration;
      }
      if (worst > 0) report("INP", worst);
    });
    po.observe({ type: "event", buffered: true, durationThreshold: 40 });
  } catch {
  }
}

// src/index.ts
var _singleton = null;
function initObservability(opts) {
  if (_singleton) return _singleton;
  const client = new LogsClient(opts);
  const crumbs = new BreadcrumbQueue(opts.breadcrumbMax ?? 50);
  if (typeof window !== "undefined") {
    installErrorHandlers(client, crumbs);
    if (opts.captureNetwork !== false) installNetworkInterceptor(client, crumbs);
    if (opts.captureWebVitals !== false) installWebVitals(client);
  }
  _singleton = client;
  return client;
}
function getClient() {
  return _singleton;
}

export {
  redact,
  LogsClient,
  BreadcrumbQueue,
  installErrorHandlers,
  installNetworkInterceptor,
  installWebVitals,
  initObservability,
  getClient
};
